import React, {useCallback, useEffect} from 'react';
import {Avatar, Grid, Stack} from "@mui/material";
import {useDashboard} from "./hooks/useDashboard";
import MensajesPorEstadoChart from "./components/MensajesPorEstadoChart";
import {DashboardFields} from "./types/dashboard";
import AtrasoPorSistemaEtapaChart from "./components/AtrasoPorSistemaEtapaChart";
import Box from "@mui/material/Box";
import {z} from "../../lib";
import {useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {AsyncSelect} from "../../components/forms";
import {HttpSystemsNewByGroup} from "../../http/group/httpSystemsNewByGroup";
import {EntityWithIdAndDescriptionFields, EntityWithIdFields} from "../../types";
import {SistemaPrincipalFields} from "../../types/workflow/cache";
import AtrasoPorSistemaChart from "./components/AtrasoPorSistemaChart";
import PromedioResolucionPorSistemaChart from "./components/PromedioResolucionPorSistemaChart";
import {HttpCacheWorkflow} from "../../http/workflow/httpCacheWorkflow";
import PromedioResolucionPorEtapaChart from "./components/PromedioResolucionPorEtapaChart";
import DashboardTablaMensajes from "./components/DashboardTablaMensajes";
import MensajesPorSistemaChart from "./components/MensajesPorSistemaChart";
import MensajesPorEtapaChart from "./components/MensajesPorEtapaChart";
import MensajesPorPeriodoChart from "./components/MensajesPorPeriodoChart";

const FormSistemaFields = {
  MensajePais: 'codPais',
  MensajeTipo: 'codMensajeTipo'
} as const

const schemaFormSistema = z.object({
  [FormSistemaFields.MensajePais]: z.number(),
  [FormSistemaFields.MensajeTipo]: z.number()
})

type FormSistema = z.infer<typeof schemaFormSistema>

function Dashboard() {
  const {data, loading, refetch} = useDashboard()

  const {control, reset, setValue, handleSubmit} = useForm<FormSistema>({
    resolver: zodResolver(schemaFormSistema),
    defaultValues: {
      [FormSistemaFields.MensajePais]: 1,
      [FormSistemaFields.MensajeTipo]: -1
    }
  });

  const watchMensajeTipo = useWatch({control: control, name: FormSistemaFields.MensajeTipo})
  const watchMensajePais = useWatch({control: control, name: FormSistemaFields.MensajePais})

  const getSistemas = useCallback(() => {
    return HttpCacheWorkflow.getSistemasPorSistemaPrincipal(watchMensajePais).then((sistemas) => {
      return (
        [{id: -1, descripcion: 'Todos los workflows'}, ...sistemas]
      )
    })
  }, [watchMensajePais])

  const opcionesSistemaPrincipal = () => HttpSystemsNewByGroup.getSystemPrincipals()
    .then(sis => (
      sis.map(opcion => (
        {
          id: opcion[EntityWithIdFields.Id],
          descripcion: opcion[EntityWithIdAndDescriptionFields.Description],
          icon: <Avatar src={opcion[SistemaPrincipalFields.SrcIcono]} sx={{height: '20px', width: '20px'}}/>
        }
      ))
    ))

  const onSubmit = (data: FormSistema) => {
    refetch(data)
  }

  useEffect(() => {
    setValue(FormSistemaFields.MensajeTipo, -1)
  },[watchMensajePais])

  useEffect(() => {
    handleSubmit(onSubmit)()
  }, [watchMensajePais, watchMensajeTipo])

  return (
    <Stack direction={'column'} gap={2}>
      <Stack direction={'row'} gap={3}>
        <AsyncSelect
          control={control}
          name={FormSistemaFields.MensajePais}
          label='País'
          loadOptions={opcionesSistemaPrincipal}
          fullWidth
          selectNotNulleable
          onlyOpenOnArrow
          valueField={'id'}
        />
        <AsyncSelect
          control={control}
          name={FormSistemaFields.MensajeTipo}
          label='Workflow'
          loadOptions={getSistemas}
          fullWidth
          selectNotNulleable
          onlyOpenOnArrow
          value={watchMensajeTipo === null ? -1 : watchMensajeTipo}
        />
      </Stack>

      <Box sx={{mt: 5}}/>

      <Grid container spacing={2}>
        <Grid item md={6} sm={12}>
          <MensajesPorEstadoChart data={data?.[DashboardFields.MensajesPorEtapaEstado] || []}/>
        </Grid>


        {
          watchMensajeTipo !== -1 ?
            <>
              <Grid item md={6} sm={12}>
                <MensajesPorEtapaChart data={data?.[DashboardFields.AtrasoPorSistemaEtapa] || []}/>
              </Grid>
              <Grid item md={6} sm={12}>
                <AtrasoPorSistemaEtapaChart data={data?.[DashboardFields.AtrasoPorSistemaEtapa] || []}/>
              </Grid>
              <Grid item md={6} sm={12}>
                <PromedioResolucionPorEtapaChart data={data?.[DashboardFields.PromedioResolucion] || []}/>
              </Grid>
            </>
            :
            <>
              <Grid item md={6} sm={12}>
                <MensajesPorSistemaChart data={data?.[DashboardFields.AtrasoPorSistema] || []}/>
              </Grid>
              <Grid item xs={12}>
                <AtrasoPorSistemaChart data={data?.[DashboardFields.AtrasoPorSistema] || []}/>
              </Grid>
              <Grid item xs={12}>
                <PromedioResolucionPorSistemaChart data={data?.[DashboardFields.PromedioResolucion] || []}/>
              </Grid>
              <Grid item xs={12}>
                <MensajesPorPeriodoChart data={data?.[DashboardFields.MensajesPorPeriodo] || []}/>
              </Grid>
            </>
        }
        {
          data &&
            <>

            <Grid item xs={12}>
                <DashboardTablaMensajes data={data[DashboardFields.Mensajes]} codPais={watchMensajePais}
                                        codSistema={watchMensajeTipo}/>
            </Grid>
            </>
        }
      </Grid>


    </Stack>
  );
}

export default Dashboard;